import React, { useState } from "react";
import { Play, RefreshCw, Database, Loader2, Snowflake, LogOut } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Dialog } from "./ui/dialog";
import { Select } from "./ui/select";
import { dayTimeOptions } from "../lib/time";

export function Topbar() {
  const { config, schedule, solve, reseed, recalculate, solving } = useApp();
  const { user, logout } = useAuth();
  const [recalcOpen, setRecalcOpen] = useState(false);
  const [freezeDay, setFreezeDay] = useState("");
  const [freezeTime, setFreezeTime] = useState("10:00");

  const status = schedule?.status;
  const statusTone = !status ? "text-muted"
    : status === "OPTIMAL" ? "text-success"
    : status === "FEASIBLE" ? "text-warning" : "text-critical";

  const dayOptions = (config?.days || []).map((d) => ({ value: d.date, label: `${d.label} · ${d.date.slice(5)}` }));
  const timeOptions = dayTimeOptions(config);
  const openRecalc = () => {
    setFreezeDay(config?.days?.[0]?.date || "");
    setRecalcOpen(true);
  };

  return (
    <header className="sticky top-0 z-40 h-16 bg-bg/95 border-b border-line flex items-center justify-between px-6">
      {solving && (
        <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden">
          <div className="h-full w-1/3 bg-primary animate-trace" />
        </div>
      )}
      <div className="flex items-center gap-4">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-faint">Planning Week</div>
          <div className="mono text-sm text-ink">
            {config ? `${config.days[0].date} → ${config.days[config.n_days - 1].date}` : "—"}
          </div>
        </div>
        <div className="h-8 w-px bg-line" />
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-faint">Solver Status</div>
          <div className={`mono text-sm font-medium ${statusTone}`}>{solving ? "SOLVING…" : status || "IDLE"}</div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={reseed} disabled={solving} data-testid="reseed-btn">
          <Database className="w-4 h-4" /> Reset demo
        </Button>
        <Button variant="outline" size="sm" onClick={openRecalc} disabled={solving} data-testid="open-recalc-btn">
          <Snowflake className="w-4 h-4" /> Freeze & recalc
        </Button>
        <Button variant="primary" size="md" onClick={solve} disabled={solving} data-testid="solve-btn">
          {solving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Generate schedule
        </Button>
        <div className="h-8 w-px bg-line mx-1" />
        <div className="hidden md:flex flex-col items-end leading-none mr-1" data-testid="topbar-user">
          <span className="mono text-[10px] uppercase tracking-widest text-faint">Signed in</span>
          <span className="mono text-xs text-muted mt-1">{user?.email || "—"}</span>
        </div>
        <Button variant="ghost" size="icon" onClick={logout} title="Sign out" data-testid="logout-btn">
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      <Dialog
        open={recalcOpen}
        onClose={() => setRecalcOpen(false)}
        title="Dynamic recalculation"
        description="Lock every slot before the freeze instant as hard constraints and re-optimise only the future."
        testid="recalc-dialog"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setRecalcOpen(false)}>Cancel</Button>
            <Button variant="primary" size="sm" data-testid="run-recalc-btn"
              onClick={async () => { setRecalcOpen(false); await recalculate(freezeDay, freezeTime); }}>
              <RefreshCw className="w-4 h-4" /> Re-optimise future
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <Select label="Freeze day" value={freezeDay} onChange={setFreezeDay} options={dayOptions} testid="freeze-day-select" />
          <Select label="Freeze before" value={freezeTime} onChange={setFreezeTime} options={timeOptions} testid="freeze-time-select" />
        </div>
        <p className="text-xs text-muted">
          The current schedule is the frozen baseline. Everything before the chosen instant stays untouched; the
          CP-SAT solver re-plans the remaining days/slots to absorb any new absences.
        </p>
      </Dialog>
    </header>
  );
}
